import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, Building, MapPin, Phone, Mail, Globe, Clock, 
  FileText, Users, CheckCircle, AlertTriangle, XCircle,
  MessageSquare, ThumbsUp, Flag, Check, Send, History, Trash2, Edit, Save, X
} from 'lucide-react';
import axios from 'axios';
import { MUNICIPIOS, tieneLocalidadesPredefinidas, getLocalidades } from '../utils/constants';
import DireccionAsistida from '../components/DireccionAsistida';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AREA_COLORS = {
  "Salud": "bg-red-100 text-red-700",
  "Desarrollo Social": "bg-blue-100 text-blue-700",
  "Educación": "bg-yellow-100 text-yellow-700",
  "Discapacidad": "bg-purple-100 text-purple-700",
  "Hábitat y Ambiente": "bg-green-100 text-green-700",
  "Niñez y Adolescencia": "bg-pink-100 text-pink-700",
  "Géneros y Diversidad": "bg-violet-100 text-violet-700",
  "Justicia": "bg-indigo-100 text-indigo-700",
  "Serv. Penitenciario": "bg-gray-100 text-gray-700",
  "Patronato Liberados": "bg-orange-100 text-orange-700",
  "Hábitat": "bg-teal-100 text-teal-700",
  "Investigación": "bg-cyan-100 text-cyan-700"
};

const ESTADO_CONFIG = {
  activa: { label: "Activa", color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle },
  dudosa: { label: "Información dudosa", color: "bg-yellow-100 text-yellow-700 border-yellow-200", icon: AlertTriangle },
  cerrada: { label: "Cerrada / Mudada", color: "bg-red-100 text-red-700 border-red-200", icon: XCircle }
};

const TIPOS_REPORTE = [
  { value: "telefono_no_funciona", label: "Teléfono no funciona" },
  { value: "direccion_incorrecta", label: "Dirección incorrecta" },
  { value: "cambio_horario", label: "Cambio de horario" },
  { value: "cerrada", label: "Institución cerrada" },
  { value: "nuevo_dato", label: "Nuevo dato para agregar" },
  { value: "otro", label: "Otro" }
];

export default function InstitucionFichaPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user, trabajador } = useAuth();
  const [institucion, setInstitucion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modo edición
  const [modoEdicion, setModoEdicion] = useState(searchParams.get('editar') === 'true');
  const [datosEdicion, setDatosEdicion] = useState(null);
  const [guardando, setGuardando] = useState(false);

  // Estados para colaboración
  const [comentarios, setComentarios] = useState([]);
  const [confirmaciones, setConfirmaciones] = useState({});
  const [historial, setHistorial] = useState([]);
  const [showHistorial, setShowHistorial] = useState(false);
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  
  // Modal de reporte
  const [showReporteModal, setShowReporteModal] = useState(false);
  const [tipoReporte, setTipoReporte] = useState('');
  const [comentarioReporte, setComentarioReporte] = useState('');
  const [enviandoReporte, setEnviandoReporte] = useState(false);

  // Estado para confirmaciones
  const [confirmando, setConfirmando] = useState(null);

  useEffect(() => {
    cargarInstitucion();
    cargarComentarios();
    cargarConfirmaciones();
  }, [id]);

  useEffect(() => {
    if (institucion && modoEdicion) {
      setDatosEdicion({
        nombre: institucion.nombre || '',
        nombre_organismo: institucion.nombre_organismo || '',
        area_desempeno: institucion.area_desempeno || '',
        telefonos: institucion.telefonos?.join(', ') || '',
        emails: institucion.emails?.join(', ') || '',
        horarios: institucion.horarios || '',
        requisitos_acceso: institucion.requisitos_acceso || '',
        web: institucion.web || '',
        demandas_atiende: institucion.demandas_atiende || '',
        indicaciones: institucion.indicaciones || '',
        // Dirección asistida
        direccion: {
          municipio: institucion.municipio || '',
          localidad: institucion.localidad || institucion.domicilio?.localidad || '',
          calle: institucion.domicilio?.calle || '',
          numero: institucion.domicilio?.numero || '',
          lat: institucion.coordenadas?.lat || null,
          lon: institucion.coordenadas?.lng || institucion.coordenadas?.lon || null,
          direccion_texto: institucion.direccion_texto || '',
          precision: institucion.precision_geocodificacion || '',
          fuente_geocodificacion: institucion.fuente_geocodificacion || ''
        }
      });
    }
  }, [institucion, modoEdicion]);

  const guardarEdicion = async () => {
    if (!datosEdicion) return;
    
    setGuardando(true);
    try {
      const dir = datosEdicion.direccion || {};
      
      const dataToSend = {
        nombre: datosEdicion.nombre || datosEdicion.nombre_organismo,
        nombre_organismo: datosEdicion.nombre_organismo,
        area_desempeno: datosEdicion.area_desempeno,
        telefonos: datosEdicion.telefonos.split(',').map(t => t.trim()).filter(Boolean),
        emails: datosEdicion.emails.split(',').map(e => e.trim()).filter(Boolean),
        domicilio: {
          calle: dir.calle || '',
          numero: dir.numero || '',
          localidad: dir.localidad || ''
        },
        municipio: dir.municipio || '',
        localidad: dir.localidad || '',
        coordenadas: dir.lat && dir.lon ? { lat: dir.lat, lng: dir.lon } : null,
        direccion_texto: dir.direccion_texto || '',
        precision_geocodificacion: dir.precision || '',
        fuente_geocodificacion: dir.fuente_geocodificacion || '',
        horarios: datosEdicion.horarios,
        requisitos_acceso: datosEdicion.requisitos_acceso,
        web: datosEdicion.web,
        demandas_atiende: datosEdicion.demandas_atiende,
        indicaciones: datosEdicion.indicaciones
      };
      
      await axios.put(`${API_URL}/instituciones/${id}`, dataToSend, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      await cargarInstitucion();
      setModoEdicion(false);
      // Limpiar parámetro de URL
      navigate(`/instituciones/${id}`, { replace: true });
    } catch (err) {
      console.error('Error guardando:', err);
      setError('Error al guardar los cambios');
    } finally {
      setGuardando(false);
    }
  };

  const cargarInstitucion = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/instituciones/${id}`);
      setInstitucion(response.data);
    } catch (err) {
      console.error('Error cargando institución:', err);
      setError('No se pudo cargar la institución');
    } finally {
      setLoading(false);
    }
  };

  const cargarComentarios = async () => {
    try {
      const response = await axios.get(`${API_URL}/instituciones/${id}/comentarios`);
      setComentarios(response.data);
    } catch (err) {
      console.error('Error cargando comentarios:', err);
    }
  };

  const cargarConfirmaciones = async () => {
    try {
      const response = await axios.get(`${API_URL}/instituciones/${id}/confirmaciones`);
      const porCampo = {};
      response.data.confirmaciones?.forEach(c => {
        porCampo[c.campo] = c;
      });
      setConfirmaciones(porCampo);
    } catch (err) {
      console.error('Error cargando confirmaciones:', err);
    }
  };

  const cargarHistorial = async () => {
    try {
      const response = await axios.get(`${API_URL}/instituciones/${id}/historial`);
      setHistorial(response.data);
    } catch (err) {
      console.error('Error cargando historial:', err);
    }
  };

  const toggleHistorial = () => {
    if (!showHistorial && historial.length === 0) {
      cargarHistorial();
    }
    setShowHistorial(!showHistorial);
  };

  const enviarComentario = async () => {
    if (!nuevoComentario.trim() || !user) return;
    
    setEnviandoComentario(true);
    try {
      await axios.post(
        `${API_URL}/instituciones/${id}/comentarios`,
        { comentario: nuevoComentario },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      setNuevoComentario('');
      cargarComentarios();
    } catch (err) {
      console.error('Error enviando comentario:', err);
    } finally {
      setEnviandoComentario(false);
    }
  };

  const marcarUtil = async (comentarioId) => {
    if (!user) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/instituciones/${id}/comentarios/${comentarioId}/util`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      // Actualizar el comentario en la lista
      setComentarios(prev => prev.map(c => 
        c.id === comentarioId 
          ? { ...c, util_count: response.data.util_count }
          : c
      ));
    } catch (err) {
      console.error('Error marcando útil:', err);
    }
  };

  const eliminarComentario = async (comentarioId) => {
    if (!user || (user.rol !== 'admin' && user.rol !== 'administracion' && user.rol !== 'superadmin')) return;
    
    if (!confirm('¿Estás seguro de que querés eliminar este comentario?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/instituciones/${id}/comentarios/${comentarioId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      // Remover el comentario de la lista
      setComentarios(prev => prev.filter(c => c.id !== comentarioId));
    } catch (err) {
      console.error('Error eliminando comentario:', err);
    }
  };

  const enviarReporte = async () => {
    if (!tipoReporte || !user) return;
    
    setEnviandoReporte(true);
    try {
      await axios.post(
        `${API_URL}/instituciones/${id}/reportar`,
        { tipo_reporte: tipoReporte, comentario: comentarioReporte },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      setShowReporteModal(false);
      setTipoReporte('');
      setComentarioReporte('');
      cargarInstitucion(); // Recargar por si cambió el estado
    } catch (err) {
      console.error('Error enviando reporte:', err);
    } finally {
      setEnviandoReporte(false);
    }
  };

  const confirmarDato = async (campo) => {
    if (!user) return;
    
    setConfirmando(campo);
    try {
      await axios.post(
        `${API_URL}/instituciones/${id}/confirmar`,
        { campo },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      cargarConfirmaciones();
      cargarInstitucion();
    } catch (err) {
      console.error('Error confirmando dato:', err);
    } finally {
      setConfirmando(null);
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    const d = new Date(fecha);
    const ahora = new Date();
    const diff = ahora - d;
    const dias = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (dias === 0) return 'Hoy';
    if (dias === 1) return 'Ayer';
    if (dias < 7) return `Hace ${dias} días`;
    if (dias < 30) return `Hace ${Math.floor(dias/7)} semanas`;
    return d.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !institucion) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-primary text-white py-4">
          <div className="max-w-4xl mx-auto px-4">
            <Button 
              variant="ghost" 
              className="text-white hover:bg-white/20"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <Building className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Institución no encontrada</h3>
              <p className="text-gray-500 mb-4">{error || 'Esta institución puede haber sido eliminada o el enlace es incorrecto.'}</p>
              <p className="text-sm text-gray-400">
                Si llegaste desde "Gestionar mi Recursero", podés desvincular este trabajo y vincularlo a otra institución.
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const estadoConfig = ESTADO_CONFIG[institucion.estado] || ESTADO_CONFIG.activa;
  const EstadoIcon = estadoConfig.icon;

  const direccionCompleta = institucion.domicilio 
    ? [
        institucion.domicilio.calle,
        institucion.domicilio.numero,
        institucion.domicilio.observaciones
      ].filter(Boolean).join(' ')
    : '';

  // Componente para mostrar confirmación de un campo
  const ConfirmacionBadge = ({ campo, label }) => {
    const conf = confirmaciones[campo];
    return (
      <div className="flex items-center gap-2 text-xs">
        {conf ? (
          <span className="text-green-600 flex items-center gap-1">
            <Check className="w-3 h-3" />
            Confirmado {formatFecha(conf.fecha)}
          </span>
        ) : (
          <span className="text-gray-400">Sin confirmar</span>
        )}
        {user && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => confirmarDato(campo)}
            disabled={confirmando === campo}
          >
            {confirmando === campo ? '...' : '✓ Confirmar'}
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          
          {user && (
            <div className="flex gap-2">
              {!modoEdicion && (
                <Button 
                  variant="outline" 
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  onClick={() => setModoEdicion(true)}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              )}
              <Button 
                variant="outline" 
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                onClick={() => setShowReporteModal(true)}
              >
                <Flag className="w-4 h-4 mr-2" />
                Informar novedad
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 animate-fade-in">
        {/* Modo Edición */}
        {modoEdicion && datosEdicion && (
          <Card className="mb-6 border-2 border-green-300">
            <CardHeader className="bg-green-50">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Edit className="w-5 h-5" />
                  Editando Institución
                </span>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setModoEdicion(false);
                      navigate(`/instituciones/${id}`, { replace: true });
                    }}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancelar
                  </Button>
                  <Button 
                    size="sm"
                    onClick={guardarEdicion}
                    disabled={guardando}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Save className="w-4 h-4 mr-1" />
                    {guardando ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre del Organismo</Label>
                  <Input
                    value={datosEdicion.nombre_organismo}
                    onChange={(e) => setDatosEdicion(prev => ({ ...prev, nombre_organismo: e.target.value }))}
                    placeholder="Ej: Ministerio Público Fiscal"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Área de Desempeño</Label>
                  <Input
                    value={datosEdicion.area_desempeno}
                    onChange={(e) => setDatosEdicion(prev => ({ ...prev, area_desempeno: e.target.value }))}
                    placeholder="Ej: Dirección de Acompañamiento a Víctimas"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Teléfonos (separados por coma)</Label>
                  <Input
                    value={datosEdicion.telefonos}
                    onChange={(e) => setDatosEdicion(prev => ({ ...prev, telefonos: e.target.value }))}
                    placeholder="Ej: 4123-4567, 4123-4568"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Emails (separados por coma)</Label>
                  <Input
                    value={datosEdicion.emails}
                    onChange={(e) => setDatosEdicion(prev => ({ ...prev, emails: e.target.value }))}
                    placeholder="Ej: contacto@org.com"
                  />
                </div>
              </div>

              {/* Dirección Asistida con Geocodificación */}
              <div className="space-y-2 border rounded-lg p-4 bg-gray-50">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Ubicación
                </Label>
                <DireccionAsistida
                  value={datosEdicion.direccion}
                  onChange={(dir) => setDatosEdicion(prev => ({ ...prev, direccion: dir }))}
                  showMap={true}
                  mapHeight="250px"
                />
              </div>

              <div className="space-y-2">
                <Label>Horarios de atención</Label>
                <Input
                  value={datosEdicion.horarios}
                  onChange={(e) => setDatosEdicion(prev => ({ ...prev, horarios: e.target.value }))}
                  placeholder="Ej: Lunes a Viernes 9-15hs"
                />
              </div>

              <div className="space-y-2">
                <Label>Requisitos de acceso</Label>
                <Textarea
                  value={datosEdicion.requisitos_acceso}
                  onChange={(e) => setDatosEdicion(prev => ({ ...prev, requisitos_acceso: e.target.value }))}
                  placeholder="Documentación o requisitos necesarios para acceder al servicio"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Sitio Web</Label>
                <Input
                  value={datosEdicion.web}
                  onChange={(e) => setDatosEdicion(prev => ({ ...prev, web: e.target.value }))}
                  placeholder="https://..."
                />
              </div>

              <div className="space-y-2">
                <Label>Demandas que atiende</Label>
                <Textarea
                  value={datosEdicion.demandas_atiende}
                  onChange={(e) => setDatosEdicion(prev => ({ ...prev, demandas_atiende: e.target.value }))}
                  placeholder="Describí las demandas que atiende el área/institución"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Indicaciones / Criterios institucionales</Label>
                <Textarea
                  value={datosEdicion.indicaciones}
                  onChange={(e) => setDatosEdicion(prev => ({ ...prev, indicaciones: e.target.value }))}
                  placeholder="Información útil para colegas"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Título y Estado */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Building className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-1">
                  {institucion.nombre_organismo || institucion.nombre}
                </h1>
                {institucion.area_desempeno && (
                  <p className="text-blue-600 font-medium">{institucion.area_desempeno}</p>
                )}
                {institucion.alias && institucion.alias.length > 0 && (
                  <p className="text-sm text-gray-500">
                    También conocida como: {institucion.alias.join(', ')}
                  </p>
                )}
              </div>
            </div>
            <Badge className={`${estadoConfig.color} border flex items-center gap-1 px-3 py-1`}>
              <EstadoIcon className="w-4 h-4" />
              {estadoConfig.label}
            </Badge>
          </div>
        </div>

        {/* Info Principal */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Información General
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Tipo de Institución</p>
                <p className="font-medium">{institucion.tipo_institucion}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Ubicación</p>
                <p className="font-medium flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  {institucion.localidad ? `${institucion.localidad}, ` : ''}
                  {institucion.municipio}
                  {institucion.barrio && ` (${institucion.barrio})`}
                </p>
              </div>
            </div>

            {institucion.areas && institucion.areas.length > 0 && (
              <div>
                <p className="text-sm text-gray-500 mb-2">Áreas de Intervención</p>
                <div className="flex flex-wrap gap-2">
                  {institucion.areas.map(area => (
                    <Badge 
                      key={area}
                      className={AREA_COLORS[area] || "bg-gray-100 text-gray-700"}
                    >
                      {area}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {direccionCompleta && (
              <div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500 mb-1">Domicilio</p>
                  <ConfirmacionBadge campo="direccion" />
                </div>
                <p className="font-medium">{direccionCompleta}</p>
              </div>
            )}

            {institucion.demandas_atiende && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-1">Demandas que atiende</p>
                <p className="text-blue-900">{institucion.demandas_atiende}</p>
              </div>
            )}

            {institucion.indicaciones && (
              <div className="p-4 bg-amber-50 rounded-lg">
                <p className="text-sm font-medium text-amber-800 mb-1">Indicaciones / Criterios institucionales</p>
                <p className="text-amber-900">{institucion.indicaciones}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contacto */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Datos de Contacto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {institucion.telefonos && institucion.telefonos.length > 0 && (
                <div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500 mb-1">Teléfonos</p>
                    <ConfirmacionBadge campo="telefono" />
                  </div>
                  {institucion.telefonos.map((tel, idx) => (
                    <p key={idx} className="font-medium flex items-center gap-1">
                      <Phone className="w-4 h-4 text-gray-400" />
                      {tel}
                    </p>
                  ))}
                </div>
              )}

              {institucion.emails && institucion.emails.length > 0 && (
                <div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500 mb-1">Emails</p>
                    <ConfirmacionBadge campo="email" />
                  </div>
                  {institucion.emails.map((email, idx) => (
                    <p key={idx} className="font-medium flex items-center gap-1">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <a href={`mailto:${email}`} className="text-primary hover:underline">
                        {email}
                      </a>
                    </p>
                  ))}
                </div>
              )}

              {institucion.web && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Sitio Web</p>
                  <p className="font-medium flex items-center gap-1">
                    <Globe className="w-4 h-4 text-gray-400" />
                    <a 
                      href={institucion.web.startsWith('http') ? institucion.web : `https://${institucion.web}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {institucion.web}
                    </a>
                  </p>
                </div>
              )}

              {institucion.whatsapp && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">WhatsApp</p>
                  <p className="font-medium flex items-center gap-1">
                    <MessageSquare className="w-4 h-4 text-gray-400" />
                    <a 
                      href={`https://wa.me/${institucion.whatsapp.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:underline"
                    >
                      {institucion.whatsapp}
                    </a>
                  </p>
                </div>
              )}
            </div>

            {institucion.horarios && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500 mb-1">Horarios de Atención</p>
                  <ConfirmacionBadge campo="horario" />
                </div>
                <p className="font-medium flex items-center gap-1">
                  <Clock className="w-4 h-4 text-gray-400" />
                  {institucion.horarios}
                </p>
              </div>
            )}

            {(!institucion.telefonos || institucion.telefonos.length === 0) && 
             (!institucion.emails || institucion.emails.length === 0) && 
             !institucion.web && !institucion.whatsapp && (
              <p className="text-gray-500 text-sm italic">
                No hay datos de contacto cargados para esta institución
              </p>
            )}
          </CardContent>
        </Card>

        {/* Requisitos de Acceso */}
        {institucion.requisitos_acceso && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Requisitos o Criterios de Acceso
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 whitespace-pre-line">{institucion.requisitos_acceso}</p>
            </CardContent>
          </Card>
        )}

        {/* Referentes */}
        {institucion.referentes && institucion.referentes.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Referentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {institucion.referentes.filter(r => r.vigente !== false).map((ref, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{ref.nombre}</p>
                      {ref.cargo && <p className="text-sm text-gray-500">{ref.cargo}</p>}
                      {ref.contacto && (
                        <p className="text-sm text-primary">{ref.contacto}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notas de Colegas */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Notas de Colegas
              {comentarios.length > 0 && (
                <Badge variant="secondary" className="ml-2">{comentarios.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Formulario para agregar nota */}
            {user ? (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">
                  Compartí información útil para colegas (ej: "Derivaciones solo con turno previo", "Piden nota institucional")
                </p>
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Escribí tu nota..."
                    value={nuevoComentario}
                    onChange={(e) => setNuevoComentario(e.target.value)}
                    rows={2}
                    className="flex-1"
                  />
                  <Button 
                    onClick={enviarComentario}
                    disabled={!nuevoComentario.trim() || enviandoComentario}
                    className="shrink-0"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-gray-500 text-sm">
                  <a href="/login" className="text-primary hover:underline">Iniciá sesión</a> para dejar una nota
                </p>
              </div>
            )}

            {/* Lista de comentarios */}
            {comentarios.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                No hay notas de colegas aún. ¡Sé el primero en compartir información útil!
              </p>
            ) : (
              <div className="space-y-4">
                {comentarios.map(c => (
                  <div key={c.id} className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-800 mb-2">&quot;{c.comentario}&quot;</p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">
                        {c.autor_nombre} • {formatFecha(c.fecha)}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`gap-1 ${c.util_count > 0 ? 'text-blue-600' : 'text-gray-500'}`}
                          onClick={() => marcarUtil(c.id)}
                          disabled={!user}
                        >
                          <ThumbsUp className="w-4 h-4" />
                          {c.util_count > 0 && <span>{c.util_count}</span>}
                          <span className="text-xs">Útil</span>
                        </Button>
                        {(user?.rol === 'admin' || user?.rol === 'administracion' || user?.rol === 'superadmin') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => eliminarComentario(c.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info de última actualización */}
        <div className="text-center text-sm text-gray-400 mt-8">
          <p>
            Última actualización: {institucion.ultima_actualizacion 
              ? new Date(institucion.ultima_actualizacion).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })
              : 'Sin datos'}
          </p>
          {user && (
            <Button
              variant="link"
              className="text-green-600 text-sm"
              onClick={() => confirmarDato('general')}
              disabled={confirmando === 'general'}
            >
              <Check className="w-4 h-4 mr-1" />
              {confirmando === 'general' ? 'Confirmando...' : 'Confirmar que la información general es correcta'}
            </Button>
          )}
          
          {/* Botón de historial */}
          <Button
            variant="link"
            className="text-gray-500 text-sm mt-2"
            onClick={toggleHistorial}
          >
            <History className="w-4 h-4 mr-1" />
            {showHistorial ? 'Ocultar historial' : 'Ver historial de cambios'}
          </Button>
        </div>

        {/* Historial de cambios */}
        {showHistorial && (
          <Card className="mb-6 mt-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Historial de Cambios
              </CardTitle>
            </CardHeader>
            <CardContent>
              {historial.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No hay cambios registrados</p>
              ) : (
                <div className="space-y-3">
                  {historial.map(h => (
                    <div key={h.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 shrink-0"></div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-800">{h.descripcion}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {h.autor_nombre} • {formatFecha(h.fecha)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Modal de Reporte */}
      {showReporteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Flag className="w-5 h-5 text-orange-500" />
                Informar novedad de la institución
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                ¿Tenés información actualizada sobre esta institución? Compartila para ayudar a otros colegas.
              </p>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Tipo de reporte</label>
                <Select value={tipoReporte} onValueChange={setTipoReporte}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccioná el tipo de problema" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_REPORTE.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Comentario (opcional)</label>
                <Textarea
                  placeholder="Agregá detalles sobre el problema..."
                  value={comentarioReporte}
                  onChange={(e) => setComentarioReporte(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowReporteModal(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={enviarReporte}
                  disabled={!tipoReporte || enviandoReporte}
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                >
                  {enviandoReporte ? 'Enviando...' : 'Enviar reporte'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
